import type { ReactNode } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  Bot,
  CalendarDays,
  Compass,
  Globe,
  MapPin,
  Search,
  Sparkles,
  Star,
  Store,
  Ticket,
  Users,
} from "lucide-react";

const TABS = [
  { id: "explore", label: "Explore", icon: Compass },
  { id: "stay", label: "Stay", icon: Store },
  { id: "experiences", label: "Experiences", icon: Sparkles },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "guides", label: "Guides", icon: Ticket },
] as const;

type TabId = (typeof TABS)[number]["id"];

// Each tab reveals a context-appropriate set of fields — the widget expands
// inline on the hero rather than navigating away.
const TAB_FIELDS: Record<TabId, { icon: LucideIcon; placeholder: string }[]> = {
  explore: [
    { icon: Search, placeholder: "Search places, monuments, cities..." },
    { icon: MapPin, placeholder: "Where to?" },
  ],
  stay: [
    { icon: MapPin, placeholder: "Destination" },
    { icon: CalendarDays, placeholder: "Check in – Check out" },
    { icon: Users, placeholder: "Guests" },
  ],
  experiences: [
    { icon: Sparkles, placeholder: "Experience type" },
    { icon: CalendarDays, placeholder: "Date" },
  ],
  events: [
    { icon: MapPin, placeholder: "City" },
    { icon: CalendarDays, placeholder: "Date" },
  ],
  guides: [
    { icon: MapPin, placeholder: "City" },
    { icon: Globe, placeholder: "Language" },
  ],
};

// Amber-fort-style hero backdrop (royalty-free Unsplash placeholder — swap later).
const HERO_IMG =
  "https://images.unsplash.com/photo-1524230572899-a752b3835840?auto=format&fit=crop&w=2000&q=80";
const CARD_IMG =
  "https://images.unsplash.com/photo-1609920658906-8223bd289001?auto=format&fit=crop&w=800&q=70";

export default function Hero() {
  const [tab, setTab] = useState<TabId>("explore");
  const fields = TAB_FIELDS[tab];

  // The AI Guide is its own public page.
  const aiGuideTo = "/ai-guide";

  return (
    <section className="relative min-h-[780px] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Heritage fort at golden hour" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/75 to-navy-900/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-transparent to-navy-900/50" />
      </div>

      <div className="container-page relative flex min-h-[780px] flex-col pt-32 pb-14">
        <div className="grid flex-1 items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          {/* Left: headline + CTAs (all share one left grid line) */}
          <div className="max-w-xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
              <Sparkles size={13} className="text-gold-400" /> AI-Powered Travel Platform
            </span>
            <h1 className="mt-5 font-display text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
              Beyond Travel.
              <br />
              <span className="text-gold-400">Into Stories.</span>
            </h1>
            <p className="mt-5 max-w-md text-base text-white/80 sm:text-lg">
              Discover India&apos;s heritage, culture, hidden gems, and authentic
              experiences with AI-powered travel assistance.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#destinations" className="btn-primary px-6 py-3 text-base shadow-iris">
                Explore Destinations <span aria-hidden>→</span>
              </a>
              <Link to={aiGuideTo} className="btn-hero-outline px-6 py-3 text-base">
                <Bot size={18} /> Talk to AI Guide
              </Link>
            </div>
          </div>

          {/* Right: floating glass card fills the previously-empty space */}
          <div className="relative hidden justify-self-end lg:block">
            <div className="animate-float w-72 rounded-3xl border border-white/15 bg-white/10 p-4 shadow-lift backdrop-blur-xl">
              <div className="relative overflow-hidden rounded-2xl">
                <img src={CARD_IMG} alt="Udaipur, City of Lakes" className="h-40 w-full object-cover" />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold-500 px-2.5 py-1 text-[11px] font-semibold text-navy-900">
                  <Sparkles size={11} /> AI Recommended
                </span>
              </div>
              <div className="mt-3 flex items-start justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-white">Udaipur</h3>
                  <p className="text-xs text-white/70">The City of Lakes</p>
                </div>
                <span className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-xs font-semibold text-gold-400">
                  <Star size={12} className="fill-gold-400 text-gold-400" /> 4.8
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
                <span className="text-sm text-white/80">
                  from <span className="font-semibold text-white">₹6,200</span>
                  <span className="text-white/60"> / night</span>
                </span>
                <Link to="/hotels" className="text-xs font-semibold text-gold-400 hover:text-gold-300">
                  View →
                </Link>
              </div>
            </div>
            {/* small offset chip for depth */}
            <div className="animate-float absolute -left-6 bottom-6 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 shadow-lift backdrop-blur-xl" style={{ animationDelay: "1.5s" }}>
              <div className="text-xs text-white/70">Trusted by</div>
              <div className="font-display text-lg font-bold text-white">10L+ travelers</div>
            </div>
          </div>
        </div>

        {/* Glassmorphism search widget — inside the hero, with breathing room */}
        <div className="mt-14">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-2 shadow-lift backdrop-blur-xl">
            <div className="flex flex-wrap gap-1 px-2 pt-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                      active ? "bg-white/10 text-gold-400" : "text-white/60 hover:text-white"
                    }`}
                  >
                    <Icon size={16} /> {t.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-1 h-px bg-white/10" />

            <div className="flex flex-col gap-2 p-2 sm:flex-row">
              {fields.map((f) => (
                <GlassField key={f.placeholder} icon={<f.icon size={16} />} placeholder={f.placeholder} />
              ))}
              <button className="btn-primary justify-center px-7 py-3 sm:shrink-0">
                <Search size={16} /> Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GlassField({ icon, placeholder }: { icon: ReactNode; placeholder: string }) {
  return (
    <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3">
      <span className="text-white/50">{icon}</span>
      <input
        type="text"
        placeholder={placeholder}
        className="w-full bg-transparent py-3 text-sm text-white placeholder:text-white/40 focus:outline-none"
      />
    </div>
  );
}